import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import type { UseQueryResult } from '@tanstack/react-query'
import { type BotPanel, type Desk, type DeskPub } from '../../../api/visitorsDesk'
import type { DeskQuery } from '../../../lib/deskPeriod'
import { pubLabel } from '../../../lib/deskFormat'
import { GroupNote } from './GroupNote'
import { fmtInt } from '../../../lib/format'
import { deskDates } from '../../../lib/deskPeriod'
import { errorDetail } from '../../../lib/visitorsFormat'
import { todayEastern } from '../../../lib/visitorsRange'

const pct = (n: number | null | undefined) => (n === null || n === undefined ? '—' : `${Math.round(n * 100)}%`)
const th = 'label text-left font-normal px-3 py-2'
const thr = 'label text-right font-normal px-3 py-2'
const tdn = 'px-3 py-2 text-right'

/** Why some visits are hidden, and how our human count lines up with GA4's. */
export function DeskBotPanel({ pub, q, bots, onClose }: {
  pub: DeskPub
  q: DeskQuery
  bots: UseQueryResult<Desk<BotPanel>>
  onClose: () => void
}) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => { ref.current?.focus() }, [])
  const d = bots.data
  const { from, to } = deskDates(q, todayEastern())
  const raw = pub === 'all' ? '' : `/visitors/${pub}/sessions?verdict=all&range=custom&from=${from}&to=${to}`
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onKeyDown={(e) => { if (e.key === 'Escape') onClose() }}>
      <div ref={ref} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="bots-h"
           className="flex max-h-full w-full max-w-3xl flex-col gap-4 overflow-y-auto border border-ink-500 bg-ink-800 p-5 text-paper outline-none">
        <div className="flex items-start justify-between gap-4">
          <h2 id="bots-h" className="font-serif text-[22px]">Humans only</h2>
          <button type="button" onClick={onClose} className="min-h-[44px] md:min-h-[32px] px-3 font-mono text-[12px] border border-ink-500 text-paper-dim hover:text-paper">Close</button>
        </div>
        {bots.isError ? (
          <p className="font-mono text-[12px] text-radar-red">{errorDetail(bots.error)}</p>
        ) : !d ? (
          <div className="h-32 bg-ink-700 animate-pulse" />
        ) : d.unavailable !== undefined ? (
          <p className="text-paper">{d.unavailable}</p>
        ) : (
          <>
            <GroupNote applied={d.group_applied} />
            <p className="text-paper">
              <strong>{pct(d.bot_share)}</strong> of visits were hidden as automated or unconfirmed ({fmtInt(d.hidden)} of {fmtInt(d.raw_visits)})
              {d.previous_share !== null && <span className="text-paper-dim"> · previous period {pct(d.previous_share)}</span>}
            </p>
            <dl className="grid grid-cols-2 gap-4 font-mono text-[12px]">
              <div><dt className="label">Unconfirmed</dt><dd>{fmtInt(d.by_verdict.unconfirmed)}</dd></div>
              <div><dt className="label">Automated</dt><dd>{fmtInt(d.by_verdict.automated)}</dd></div>
            </dl>
            {d.reasons.length > 0 && (
              <div>
                <h3 className="label mb-2">Top reasons</h3>
                <ul className="font-mono text-[12px]">
                  {d.reasons.map((r) => <li key={r.reason} className="py-1">{r.reason} · {fmtInt(r.visits)}</li>)}
                </ul>
              </div>
            )}
            <div>
              <h3 className="label mb-2">First-party people against GA4 humans</h3>
              {d.reconciliation.length === 0 && <p className="font-mono text-[12px] text-paper-faint">No reconciliation days yet</p>}
              {d.reconciliation.map((rec) => (
                <div key={rec.publication} className="mb-4">
                  <p className="font-mono text-[12px] text-paper-dim mb-1">{pubLabel(rec.publication)} · {rec.flagged_days} flagged</p>
                  <div className="overflow-x-auto">
                    <table className="w-full font-mono text-[12px]">
                      <thead>
                        <tr className="border-b border-ink-500">
                          <th className={th}>Day</th><th className={thr}>First-party</th><th className={thr}>GA4 human</th>
                          <th className={thr}>Drift</th><th className={th}>Flag</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rec.rows.map((r) => (
                          <tr key={r.day} className="border-b border-ink-500/50">
                            <td className="px-3 py-2">{r.day}</td>
                            <td className={tdn}>{r.first_party_people === null ? '—' : fmtInt(r.first_party_people)}</td>
                            <td className={tdn}>{r.ga4_people === null ? '—' : fmtInt(r.ga4_people)}</td>
                            <td className={tdn}>{r.comparable ? pct(r.drift) : '—'}</td>
                            <td className="px-3 py-2">
                              {!r.comparable ? <span className="text-paper-faint">Not comparable</span>
                                : r.flagged ? <span className="text-radar-red">⚑ Drift over 25%</span> : null}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
              {d.reconciliation_note && <p className="text-[11px] text-paper-faint">{d.reconciliation_note}</p>}
            </div>
            <p className="text-[11px] text-paper-faint">{d.note}</p>
            {raw && <Link to={raw} className="font-mono text-[12px] text-amber-glow hover:underline">See the raw visits ({fmtInt(d.raw_visits)}) →</Link>}
          </>
        )}
      </div>
    </div>
  )
}
